import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import "./style.scss";
import { CardSvgPPP, CoinsStackSvgPPP, ExclamationIconSvgPPP, GreenTickIconSvgPPP, PaperSvgPPP } from "../svgImages";
import { IPaymentPayload } from "../..";
import { debounce } from "lodash";
import { verifyGstNumber } from "../../../../api/verification.api";
import GstDetailsCard from "../GstDetailsCard";

interface ITaxInfoCard {
  editAccess: boolean;
  fieldsDisabled: boolean;
  totalAmount: number;
  paymentPayload: IPaymentPayload;
  setPaymentPayload: Dispatch<SetStateAction<IPaymentPayload>>;
  paymentGoAhead: boolean;
  setPaymentGoAhead: Dispatch<SetStateAction<boolean>>;
  gstErrorsObj: { gstNumber: boolean, gstName: boolean};
  setGstErrorsObj: Dispatch<SetStateAction<any>>;
}

const TaxInfoCard: React.FC<ITaxInfoCard> = (props) => {
  const { editAccess, fieldsDisabled, totalAmount, paymentPayload, setPaymentPayload, paymentGoAhead, setPaymentGoAhead, gstErrorsObj, setGstErrorsObj } = props;

  const [tabSwitch, setTabSwitch] = useState<1 | 2>(1);
  const [viewSwitch, setViewSwitch] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleTCSTabClick = () => { 
    if (!editAccess) return;
    setTabSwitch(1);
    setPaymentPayload({ ...paymentPayload, tcsSlab: 20 });
  }
  const handleTDSTabClick = () => {
    if (!editAccess) return;
    setTabSwitch(2);
    let tempObj: IPaymentPayload;
    tempObj = { ...paymentPayload };
    delete tempObj?.tcsSlab;
    setPaymentPayload({ ...tempObj });
    setPaymentGoAhead(false);
  }

  const handleExpenseCheckClick = (e: any) => {
    setPaymentPayload({ ...paymentPayload, tcsSlab: e.target.checked ? 5 : 20 });
  };

  const handleRegimeRadioClick = (e: any) => {
    setPaymentPayload({ ...paymentPayload, taxRegime: e.target.value });
  };

  const debouncedFn = debounce(async (value) => {
    const status = await verifyGstNumber(value);
    setLoading(false);
    setGstErrorsObj({ ...gstErrorsObj, gstNumber: !status });
  }, 500);

  const handleGstInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const trimmedValue = name === "gstNumber" ? value?.toUpperCase().trim() : value?.toUpperCase();
    setPaymentPayload((prevPayload) => ({ ...prevPayload, [name]: trimmedValue }));

    const gstNumberRegex = /^\d{2}[a-zA-Z]{5}\d{4}[a-zA-Z]\d[Zz][a-zA-Z\d]$/;
    const gstNameRegex = /^[a-zA-Z\s]+$/;

    let errorTriggered = false;

    if (name === "gstNumber") {
      setGstErrorsObj({ ...gstErrorsObj, gstNumber: !gstNumberRegex.test(trimmedValue) });
      errorTriggered = !gstNumberRegex.test(trimmedValue);
      if (errorTriggered) {
        setLoading(false);
        debouncedFn.cancel();
      } else {
        setLoading(true);
        debouncedFn(trimmedValue);
      }      
    } else {
      setGstErrorsObj({...gstErrorsObj, gstName: !gstNameRegex.test(trimmedValue)});
      errorTriggered = !gstNameRegex.test(trimmedValue);
    }

    if (errorTriggered) setPaymentGoAhead(false);

    // to check if both fields are empty then reset the error
    const otherField = name === "gstNumber" ? "gstName" : "gstNumber";
    if (value === '' && !paymentPayload[otherField]) {
      setGstErrorsObj({ gstNumber: false, gstName: false });
    }
  }, [paymentPayload, gstErrorsObj]);

  useEffect(() => {
    if (paymentGoAhead && paymentPayload?.tcsSlab) setTabSwitch(1);
    else if (paymentGoAhead && !paymentPayload?.tcsSlab) setTabSwitch(2);
  }, [paymentGoAhead])
  
  return (
    <div className="tax-info-card-wrapper">
      <div className="tax-info-tab-wrapper">
        <div className={`tcs-tab-wrapper ${tabSwitch === 1 ? "active" : "inactive"}`} onClick={handleTCSTabClick}>
          <span>TCS (Tax Collected at Source)</span>
        </div>
        <div className={`tds-tab-wrapper ${tabSwitch === 2 ? "active" : "inactive"}`} onClick={handleTDSTabClick}>
          <span>TDS (Tax Deducted at Source)</span>
        </div>
      </div>
      <div className="tax-info-body-wrapper">
        {tabSwitch === 1 && (
          <div className="travel-expense-wrapper">
            <label>
              <input
                type="checkbox"
                name="travel-expense"
                disabled={fieldsDisabled || totalAmount >= 700000}
                onChange={handleExpenseCheckClick}
                checked={paymentPayload?.tcsSlab === 5}
              />
              <span className="label-text">If Travel Expenses less than 7 Lakhs?</span>
            </label>
            <p className={`declaration ${paymentPayload?.tcsSlab === 5 ? "active" : ""}`}>
              I declare that my current Financial Year's Travel Expense is &lt; (less than) 7 Lakhs and I agree to pay{" "}
              <span>5% TCS (Tax Collected at Source)</span> that is applicable on the <span>Grand Total.</span>
            </p>
            <p className="notice">
              <span className="black-bold">Important Notice:</span> If your Current Financial Year's Travel Expense{" "}
              <span className="black-bold">exceeds 7 Lakhs</span> then a{" "}
              <span className="orange-bold">20% TCS (Tax Collected at Source)</span> will be imposed on the{" "}
              <span className="black-bold">Grand Total.</span>
            </p>
            {viewSwitch && (
              <div className="view-more-body-wrapper">
                <div className="top-section-wrapper">
                  <div className="view-more-card">
                    <div className="card-svg">
                      <CardSvgPPP width={21} />
                    </div>
                    <div className="view-more-info">
                      <span className="primary-text">Tax charge</span>
                      <span className="secondary-text">
                        Tax collection at the source is mandatory as per government regulations
                      </span>
                    </div>
                  </div>
                  <div className="view-more-card">
                    <div>
                      <CoinsStackSvgPPP width={21} />
                    </div>
                    <div className="view-more-info">
                      <span className="primary-text">Claiming your credit</span>
                      <span className="secondary-text">
                        TCS collected is claimable as a credit against the tax payable during return filing. (Applicable
                        for Indians Only)
                      </span>
                    </div>
                  </div>
                </div>
                <div className="view-more-card bottom-section-wrapper">
                  <div>
                    <PaperSvgPPP width={21} />
                  </div>
                  <div className="view-more-info">
                    <span className="primary-text">TCS is collected via Cordelia Cruise</span>
                    <span className="secondary-text">
                      TCS credits reflect quarterly in Form 26AS, detailing deductions, collections, and payments,
                      including seller-collected TCS. These updates aid in tracking and verifying tax statements.
                    </span>
                  </div>
                </div>
              </div>
            )}
            <span className="view-switch" onClick={() => setViewSwitch((prev) => !prev)}>
              {viewSwitch ? "View Less" : "View More"}
            </span>
          </div>
        )}
        <div className="tax-radio-wrapper">
          <span className="heading-text">Select Regime</span>
          <div className="regimes-radio-wrapper">
            <div className={`old-regime-radio-wrapper ${paymentPayload?.taxRegime === "OLD" ? "active" : "inactive"}`}>
              <label>
                <input
                  type="radio"
                  disabled={fieldsDisabled}
                  name="regime-type"
                  onChange={handleRegimeRadioClick}
                  value={"OLD"}
                  checked={paymentPayload?.taxRegime === "OLD"}
                />
                <span className="label-text">Old Tax Regime</span>
              </label>
            </div>
            <div className={`new-regime-radio-wrapper ${paymentPayload?.taxRegime === "NEW" ? "active" : "inactive"}`}>
              <label>
                <input
                  type="radio"
                  disabled={fieldsDisabled}
                  name="regime-type"
                  onChange={handleRegimeRadioClick}
                  value={"NEW"}
                  checked={paymentPayload?.taxRegime === "NEW"}
                />
                <span className="label-text">New Tax Regime</span>
              </label>
            </div>
          </div>
        </div>
        <GstDetailsCard
          fieldsDisabled={fieldsDisabled}
          paymentPayload={paymentPayload}
          setPaymentPayload={setPaymentPayload}
          paymentGoAhead={paymentGoAhead}
          setPaymentGoAhead={setPaymentGoAhead}
          gstErrorsObj={gstErrorsObj}
          setGstErrorsObj={setGstErrorsObj}
        />
      </div>
    </div>
  );
};

export default TaxInfoCard;
